import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Card } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Search } from "lucide-react";

export default function Pricing() {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [rates, setRates] = useState([]);
  const [grid, setGrid] = useState({}); // key: cid:pid -> price
  const [dirty, setDirty] = useState(new Set());
  const [saving, setSaving] = useState(false);

  // Search & Pagination states
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  useEffect(() => {
    Promise.all([api.get("/products"), api.get("/customers"), api.get("/rates")])
      .then(([p, c, r]) => {
        setProducts(p.data); setCustomers(c.data); setRates(r.data);
        const g = {};
        for (const x of r.data) g[`${x.customer_id}:${x.product_id}`] = x.selling_price;
        setGrid(g);
      });
  }, []);

  // Filter customers by search term
  const filteredCustomers = useMemo(() => {
    return customers.filter(c =>
      !q ||
      c.business_name.toLowerCase().includes(q.toLowerCase()) ||
      c.customer_name.toLowerCase().includes(q.toLowerCase()) ||
      (c.mobile || "").includes(q)
    );
  }, [customers, q]);

  // Pagination logic
  const totalItems = filteredCustomers.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const paginatedCustomers = filteredCustomers.slice(startIdx, startIdx + pageSize);

  // Reset page when search or page size changes
  useEffect(() => { setPage(1); }, [q, pageSize]);

  function setCell(cid, pid, val) {
    const k = `${cid}:${pid}`;
    setGrid(prev => ({ ...prev, [k]: val }));
    setDirty(prev => new Set(prev).add(k));
  }

  async function saveAll() {
    if (dirty.size === 0) { toast.info("Nothing to save"); return; }
    setSaving(true);
    const payload = { rates: [] };
    for (const k of dirty) {
      const [customer_id, product_id] = k.split(":");
      const v = parseFloat(grid[k]);
      if (!isNaN(v)) payload.rates.push({ customer_id, product_id, selling_price: v });
    }
    try {
      await api.post("/rates/bulk", payload);
      toast.success(`Saved ${payload.rates.length} prices`);
      setDirty(new Set());
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  }

  return (
    <div data-testid="pricing-page">
      <PageHeader
        eyebrow="Per-Customer Pricing"
        title="Pricing Matrix"
        description="Set unique rates for every product per customer. Edit cells and click save."
        actions={
          <Button onClick={saveAll} disabled={saving || dirty.size === 0} className="bg-cobalt-500 hover:bg-cobalt-700 text-white" data-testid="pricing-save-btn">
            <Save size={16} className="mr-2"/> Save{dirty.size > 0 ? ` (${dirty.size})` : ""}
          </Button>
        }
      />

      <Card padding="p-0">
        {/* Header Controls: Search + Page Size */}
        <div className="p-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
          <div className="relative w-full sm:w-80">
            <Search size={14} className="absolute left-3 top-3 text-slate-400"/>
            <Input
              data-testid="pricing-search"
              placeholder="Search customer by name or business…"
              className="pl-9 text-xs"
              value={q}
              onChange={e=>setQ(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Show:</span>
            <Select value={String(pageSize)} onValueChange={v => setPageSize(Number(v))}>
              <SelectTrigger className="w-28 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 per page</SelectItem>
                <SelectItem value="15">15 per page</SelectItem>
                <SelectItem value="25">25 per page</SelectItem>
                <SelectItem value="50">50 per page</SelectItem>
                <SelectItem value="100">100 per page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {paginatedCustomers.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No matching customers found.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="ddms-table" data-testid="pricing-matrix-table">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-slate-100 z-10" style={{minWidth: 220}}>Customer</th>
                    {products.map(p => (
                      <th key={p.id} className="num">
                        <div className="flex flex-col items-end gap-1">
                          {p.image_url ? (
                            <img src={resolveImage(p.image_url)} alt={p.product_name} className="w-8 h-8 rounded-md object-cover border border-slate-200"/>
                          ) : null}
                          <span>{p.product_name}</span>
                          <span className="text-[10px] text-slate-400 font-normal">/{p.unit}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedCustomers.map((c, idx) => (
                    <tr key={c.id} className={idx % 2 === 0 ? "" : "bg-slate-50/50"}>
                      <td className="sticky left-0 bg-inherit font-medium">
                        <div className="text-slate-900">{c.business_name}</div>
                        <div className="text-[11px] text-slate-500">{c.business_type}</div>
                      </td>
                      {products.map(p => {
                        const k = `${c.id}:${p.id}`;
                        const isDirty = dirty.has(k);
                        return (
                          <td key={p.id} className="num">
                            <div className="relative">
                              <span className="absolute left-2 top-2 text-xs text-slate-400">₹</span>
                              <input
                                type="number"
                                step="0.01"
                                value={grid[k] ?? ""}
                                data-testid={`rate-${c.id}-${p.id}`}
                                onChange={(e) => setCell(c.id, p.id, e.target.value)}
                                className={`w-24 pl-5 pr-2 py-1.5 text-right rounded border ${isDirty ? "border-cobalt-500 bg-cobalt-50" : "border-slate-200 bg-white"} font-mono text-sm focus:outline-none focus:ring-2 focus:ring-cobalt-500/30`}
                                placeholder="—"
                              />
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls Footer */}
            <div className="p-4 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-600">
              <div>
                Showing <span className="font-semibold text-slate-900">{startIdx + 1}</span> to <span className="font-semibold text-slate-900">{Math.min(startIdx + pageSize, totalItems)}</span> of <span className="font-semibold text-slate-900">{totalItems}</span> customers
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={currentPage === 1}
                  onClick={() => setPage(p => Math.max(p - 1, 1))}
                  className="h-8 px-3 text-xs"
                >
                  Previous
                </Button>
                <div className="px-3 py-1 font-medium text-slate-800">
                  Page {currentPage} of {totalPages}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage(p => Math.min(p + 1, totalPages))}
                  className="h-8 px-3 text-xs"
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
